import React, { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { api } from "../api";

export default function Signup() {
  const navigate = useNavigate();
  const location = useLocation();
  const membershipType = location.state?.membershipType || "";

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const goNext = (user) => {
  localStorage.setItem("member", JSON.stringify(user));
  window.dispatchEvent(new Event("authChanged"));

  navigate("/membershipform", {
    state: { membershipType },
  });
};

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setMessage("");

        const result = await api.post("googleLogin.php", {
          access_token: tokenResponse.access_token,
        });

        if (result?.success && result?.user) {
          setMessage("Google signup successful! You are now logged in.");

          setTimeout(() => {
            goNext(result.user);
          }, 1000);
        } else {
          setMessage(result?.message || "Google signup failed");
        }
      } catch (err) {
        console.error("Google signup error:", err);
        setMessage("Google signup error");
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setMessage("Google sign-up failed.");
    },
  });

  const handleSignup = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      setMessage("Passwords do not match");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const signupResult = await api.post("registerMember.php", {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        auth_provider: "manual",
      });

      // New signup success -> auto login + redirect
      if (signupResult?.success && signupResult?.user) {
        setMessage("Signup successful! You are now logged in.");

        setTimeout(() => {
          goNext(signupResult.user);
        }, 1000);

        return;
      }

      // If user already exists, try login with same credentials
      if (
        signupResult?.message?.toLowerCase().includes("email already exists")
      ) {
        const loginResult = await api.post("loginMember.php", {
          email: formData.email,
          password: formData.password,
        });

        if (loginResult?.success && loginResult?.user) {
          setMessage("Account already exists. Logged in successfully.");

          setTimeout(() => {
            goNext(loginResult.user);
          }, 1000);
        } else {
          setMessage(
            loginResult?.message ||
              "Account already exists. Please sign in with your correct password."
          );
        }

        return;
      }

      setMessage(signupResult?.message || "Signup failed");
    } catch (error) {
      console.error("Signup error:", error);
      setMessage(error.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftSignup = async () => {
    setMessage("Microsoft signup integration is next.");
  };

  const controlClass =
    "w-full h-[48px] px-4 rounded-lg border border-gray-300 text-sm flex items-center justify-center gap-3 hover:bg-gray-50 transition disabled:opacity-60";

  return (
    <section className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-100">
      <div className="w-full max-w-md bg-white p-6 rounded-xl shadow">
        <h1 className="text-2xl font-bold text-center mb-2">Create Account</h1>

        {membershipType && (
          <p className="text-center text-sm text-gray-600 mb-4">
            Selected membership: <strong>{membershipType}</strong>
          </p>
        )}

        <div className="space-y-3 mb-6">
          <div className="w-full space-y-3">
            <button
              type="button"
              onClick={() => googleLogin()}
              className={controlClass}
              disabled={loading}
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-5 h-5"
              />
              <span className="text-gray-700">Continue with Google</span>
            </button>

            <button
              type="button"
              onClick={handleMicrosoftSignup}
              className={controlClass}
              disabled={loading}
            >
              <div className="grid grid-cols-2 gap-[2px] w-5 h-5">
                <div className="bg-[#F25022]" />
                <div className="bg-[#7FBA00]" />
                <div className="bg-[#00A4EF]" />
                <div className="bg-[#FFB900]" />
              </div>
              <span className="text-gray-700">Continue with Microsoft</span>
            </button>
          </div>
        </div>

        <div className="relative my-6">
          <div className="border-t" />
          <span className="absolute left-1/2 -translate-x-1/2 -top-3 bg-white px-3 text-sm text-gray-500">
            Or sign up with email
          </span>
        </div>

        <form onSubmit={handleSignup} className="space-y-4">
          <input
            type="text"
            name="full_name"
            placeholder="Full Name"
            value={formData.full_name}
            onChange={handleChange}
            className="w-full h-[45px] border px-3 rounded"
            required
          />

          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className="w-full h-[45px] border px-3 rounded"
            required
          />

          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleChange}
            className="w-full h-[45px] border px-3 rounded"
            required
          />

          <input
            type="password"
            name="confirm_password"
            placeholder="Confirm Password"
            value={formData.confirm_password}
            onChange={handleChange}
            className="w-full h-[45px] border px-3 rounded"
            required
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full h-[45px] bg-green-600 text-white rounded font-semibold disabled:opacity-60"
          >
            {loading ? "Creating..." : "Sign Up"}
          </button>
        </form>

        {message && (
          <p
            className={`text-center text-sm mt-3 ${
              message.toLowerCase().includes("successful") ||
              message.toLowerCase().includes("logged in")
                ? "text-green-600"
                : "text-red-500"
            }`}
          >
            {message}
          </p>
        )}

        <p className="text-center text-sm mt-4">
          Already have an account?{" "}
          <Link
            to="/login"
            state={{ membershipType }}
            className="text-blue-600 font-semibold"
          >
            Sign In
          </Link>
        </p>
      </div>
    </section>
  );
}